// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { createFragmentContainer } from "react-relay";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";

// internal
import { IProps, ISingleTeamConfirmationProps, IState } from "./OnboardingTeamJoinCreateStep.d";
import { styles } from "./OnboardingTeamJoinCreateStep.styles";
import { fragments } from "./OnboardingTeamJoinCreateStep.gql";
import { OnboardingType } from "@components/onboarding-nux/OnboardingNUX.d";

// components
import { A, P } from "@components/ui";
import { OnboardingSplitScreenStep } from "@components/onboarding-nux/onboarding-steps/onboarding-split-screen-step";
import { OnboardingButtons } from "@components/onboarding-nux/onboarding-buttons";
import { TeamJoinCreate } from "@components/team-join-create";
import { Button } from "@material-ui/core";
import { AvatarRow } from "@components/avatar-row";

// util
import { fromGlobalId } from "@util/graphql.util";
import { track, TrackingEvents, trackFSEvent } from "@util/analytics.util";
import { niceNameArrayFromPersonsWithCarryover } from "@clockwise/client-commons/src/util/event";
import { PostMessageManager } from "@util/post-message.util";

////////////////////////////////
// Helper & Shared Components //
////////////////////////////////

function getSingleTeamFromOrg(org: ISchema.IOrg) {
  return (
    (org &&
      org.userTeams &&
      org.userTeams.__typename !== "GraphEntityError" &&
      org.userTeams.list.length !== 0 &&
      org.userTeams.list[0]) ||
    undefined
  );
}

export function OnboardingTeamJoinCreateStepDisplayPaneComponent(props: WithStyles<typeof styles>) {
  const { classes } = props;

  return (
    <div className={classes.display}>
      <div className={classes.illustration} />
      <div className={classes.checkList}>
        <div>
          <div className="check">🗓</div>
          <div className="text">
            Get visibility into your team's capacity with a team availability calendar
          </div>
        </div>
        <div>
          <div className="check">📊</div>
          <div className="text">Forecast your team’s bandwidth with real-time team analytics</div>
        </div>
        <div>
          <div className="check">💡</div>
          <div className="text">Automatically protect team focus days with no meeting days</div>
        </div>
      </div>
    </div>
  );
}

export const OnboardingTeamJoinCreateDisplayPane = withStyles(styles)(
  OnboardingTeamJoinCreateStepDisplayPaneComponent,
);

export function OnboardingTeamJoinCreateSingleTeamConfirmationComponent(
  props: ISingleTeamConfirmationProps,
) {
  const { classes, viewer, onBypass, onClickDone, onClickSettings, team } = props;

  const teamName = team.teamName;

  const youCreated = team.createdBy?.value === fromGlobalId(viewer.user.id).id;

  const allTeamMembers = [...(team.invitedMembers || []), ...(team.teamMembers || [])];

  const persons = allTeamMembers.map((tm) => tm.person);
  const you = allTeamMembers.find((tm) => tm.person.isYou);
  const maybeInviter = you && allTeamMembers.find((tm) => tm.person.userId === you.addedBy);

  const niceNames = niceNameArrayFromPersonsWithCarryover(
    persons,
    undefined,
    40,
    true,
    undefined,
    true,
  ).niceNameArray.join("");

  return (
    <div className={classes.singleContainer}>
      <div className={classes.emoji}>🙌</div>
      <div className={`${classes.header} single-team`}>
        {youCreated ? (
          <div>
            Welcome! It looks like you already created the team <br /> "{teamName}."
          </div>
        ) : !!maybeInviter && maybeInviter?.person?.profile?.givenName ? (
          <div>
            Welcome! {maybeInviter.person.profile.givenName}{" "}
            {maybeInviter.person.profile?.familyName ?? ""} has added you to the team "{teamName}."
          </div>
        ) : (
          <div>
            Welcome! You have been added to the team <br /> "{teamName}."
          </div>
        )}
      </div>
      <AvatarRow orgPersons={persons} size="large" overlap showTooltip maxAvatarCount={6} />
      <P style={{ marginTop: 26, marginBottom: 26, ...styles.singleParagraph }}>
        {niceNames
          ? `Your teammates include ${niceNames}. You can view or manage your team anytime later in `
          : `You can view or manage your team anytime later in `}
        {onClickSettings ? <A onClick={onClickSettings}>settings</A> : "settings"}.
      </P>
      {onClickDone ? (
        <Button
          className={classes.doneButton}
          onClick={onClickDone}
          variant="contained"
          color="primary"
        >
          Done
        </Button>
      ) : null}
      {youCreated && onBypass ? (
        <P style={{ fontWeight: "bold", ...styles.singleParagraph }}>
          Or, you can <A onClick={onBypass}>set up a new team</A>
        </P>
      ) : null}
    </div>
  );
}

export const OnboardingTeamJoinCreateSingleTeamConfirmation = withStyles(styles)(
  OnboardingTeamJoinCreateSingleTeamConfirmationComponent,
);

////////////////////
// Main Component //
////////////////////

class OnboardingTeamJoinCreateStepComponent extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      currentView: "create",
      joinMode: "off",
      isValid: false,
      saving: false,
    };
  }

  /////////////
  // Helpers //
  /////////////

  private isSkipButtonHidden = () => {
    const { joinMode, currentView } = this.state;

    if (joinMode === "single-team") {
      return true;
    } else if (joinMode === "select-primary") {
      return true;
    } else if (currentView === "create") {
      return false;
    }
    return false;
  };

  private getForwardButtonLabel = () => {
    const { org } = this.props;
    const { joinMode, currentView } = this.state;

    if (joinMode === "single-team" && getSingleTeamFromOrg(org)) {
      return "Got It";
    } else if (joinMode === "select-primary") {
      return "Confirm";
    } else if (currentView === "create") {
      return "Create Team";
    }
    return "Join Team";
  };

  private isForwardButtonDisabled = () => {
    const { org } = this.props;
    const { joinMode } = this.state;

    if (joinMode === "single-team" && getSingleTeamFromOrg(org)) {
      return false;
    }

    return !this.state.isValid;
  };

  private onFail = () => {
    this.setState({
      saving: false,
    });
  };

  private onPreSave = () => {
    this.props.goForward();
  };

  private onSave = () => {
    this.props.refetch();
  };

  private goForward = () => {
    if (this.state.joinMode === "single-team") {
      const userTeams =
        (this.props.org?.userTeams &&
          this.props.org.userTeams.__typename !== "GraphEntityError" &&
          this.props.org?.userTeams.list) ||
        [];

      userTeams.forEach((team) => {
        if (
          team.teamCalendarId &&
          team.settings &&
          team.settings.__typename !== "GraphEntityError" &&
          team.settings.manageTeamCalendar &&
          team.userSettings &&
          team.userSettings.__typename !== "GraphEntityError" &&
          team.userSettings.useTeamCalendar
        ) {
          PostMessageManager.toggleCalendar(team.teamCalendarId, true);
        }
      });

      this.props.goForward();
      return;
    }

    this.setState({
      saving: true,
    });
  };

  public onInit = ({
    nDisplayedTeams,
    nSuggestedTeams,
    nDisplayedInviteSuggestions,
  }: {
    nDisplayedTeams: number;
    nSuggestedTeams: number;
    nDisplayedInviteSuggestions: number;
  }) => {
    const { org } = this.props;

    const userTeams =
      (org?.userTeams?.__typename !== "GraphEntityError" && org.userTeams.list) || [];

    track(TrackingEvents.ONBOARDING.ONBOARDING_TEAM_SHOWN, {
      nDisplayedTeams,
      nSuggestedTeams,
      nDisplayedInviteSuggestions,
      isInvited: userTeams.length > 0,
      nInvitedTeams: userTeams.length,
    });
  };

  ///////////////
  // Lifecycle //
  ///////////////

  public UNSAFE_componentWillMount() {
    trackFSEvent(TrackingEvents.ONBOARDING.ONBOARDING_TEAM_SHOWN);
  }

  ///////////////
  // Rendering //
  ///////////////

  protected renderButtons(): JSX.Element | null {
    return (
      <OnboardingButtons
        forwardButtonLabel={this.getForwardButtonLabel()}
        forwardButtonDisabled={this.isForwardButtonDisabled()}
        skipButtonHidden={this.isSkipButtonHidden()}
        skipButtonLabel="Not Now"
        onClickSkipButton={this.props.goForward}
        onClickBackButton={this.props.goBack}
        onClickForwardButton={this.goForward}
      />
    );
  }

  private renderConfiguration = () => {
    const { org, viewer, onboardingMeta } = this.props;
    const onboardingMode =
      onboardingMeta.onboardingType === OnboardingType.ManagerSegmentation
        ? "onboarding-manager"
        : "onboarding";

    return (
      <TeamJoinCreate
        org={org}
        viewer={viewer}
        mode={onboardingMode}
        analyticsMode="onboarding"
        onInit={this.onInit}
        onChangeValidity={(isValid) => this.setState({ isValid })}
        onChangeView={(currentView) => this.setState({ currentView })}
        onChangeJoinMode={(joinMode) => {
          if (joinMode === "single-team") {
            const singleTeam = getSingleTeamFromOrg(org);

            const teamId = singleTeam?.teamId;

            const youCreated = singleTeam?.createdBy?.value === fromGlobalId(viewer.user.id).id;

            track(TrackingEvents.ONBOARDING.ONBOARDING_TEAM_CONFIRMATION_SHOWN, {
              teamId,
              userWasCreator: youCreated,
            });
          }

          this.setState({ joinMode });
        }}
        onFail={this.onFail}
        onSave={this.onSave}
        onPreSave={this.onPreSave}
        save={this.state.saving}
        minimumRequiredInvitesToCreate={1}
      />
    );
  };

  private renderDisplay = () => {
    return <OnboardingTeamJoinCreateDisplayPane />;
  };

  private renderSingleTeamConfirmation = (team: ISchema.ITeam) => {
    const { org, viewer } = this.props;

    return <OnboardingTeamJoinCreateSingleTeamConfirmation org={org} viewer={viewer} team={team} />;
  };

  public render() {
    const { classes, currentStep, goTo, lockedStepMap, org, onboardingMeta } = this.props;
    const { joinMode } = this.state;

    const pillsClassName =
      ((joinMode === "off" || joinMode === "select-primary") && " no-pills") || "";

    const singleTeam = getSingleTeamFromOrg(org);

    if (joinMode === "single-team" && singleTeam) {
      return (
        <React.Fragment>
          <OnboardingSplitScreenStep
            lockedStepMap={lockedStepMap}
            currentStep={currentStep}
            onboardingMeta={onboardingMeta}
            goTo={goTo}
            displayPaneClassName={classes.displayPane}
            configurationPaneChildren={this.renderSingleTeamConfirmation(singleTeam)}
            configurationPaneClassName={`${classes.configurationPane}${pillsClassName}`}
          >
            {this.renderDisplay()}
          </OnboardingSplitScreenStep>
          {this.renderButtons()}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <OnboardingSplitScreenStep
          lockedStepMap={lockedStepMap}
          currentStep={currentStep}
          onboardingMeta={onboardingMeta}
          goTo={goTo}
          displayPaneClassName={classes.displayPane}
          configurationPaneChildren={this.renderConfiguration()}
          configurationPaneClassName={`${classes.configurationPane}${pillsClassName}`}
        >
          {this.renderDisplay()}
        </OnboardingSplitScreenStep>
        {this.renderButtons()}
      </React.Fragment>
    );
  }
}

/////////////////
// With Styles //
/////////////////

const OnboardingTeamJoinCreateStepWithStyles = withStyles(styles)(
  OnboardingTeamJoinCreateStepComponent,
);

///////////
// Relay //
///////////

export const OnboardingTeamJoinCreateStep = createFragmentContainer(
  OnboardingTeamJoinCreateStepWithStyles,
  fragments,
);
